import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { BehaviorSubject } from "rxjs"

/**
 * Each time a new storageKey is addressed, a new BehaviorSubject is built.
 * Then, any call to useLocalStorage will reuse the same observable, so updating the localStorage
 * in a component will rerender other components that rely on it, while persisting the state in storage.
 */
const localStorageObservables: { [storageKey: string]: BehaviorSubject<string> } = {}

export function useLocalStorage<T extends string> (storageKey: string, defaultValue?: T): [T, Dispatch<SetStateAction<T>>] {
  // use setState, so react is aware when to render the caller component (when this data changes)
  const [value, setValue] = useState(localStorage.getItem(storageKey) as T)

  // use a unique observable per storageKey, so every component listening the same `storageKey` will register a shared state
  localStorageObservables[storageKey] ||= new BehaviorSubject(value as string)

  useEffect(() => {
    // update the caller component when any intance of the hook for that `storageKey` does a mutation.
    const subscription = localStorageObservables[storageKey].subscribe(setValue as Dispatch<SetStateAction<string>>)
    return () => subscription.unsubscribe()
  }, [storageKey])

  const currentValue = value ?? defaultValue
  return [currentValue, newValue => {
    const resolved = typeof newValue === 'function' ? newValue(currentValue) : newValue
    localStorage.setItem(storageKey, resolved)
    localStorageObservables[storageKey].next(resolved)
  }]
}

export function useLocalStorageBoolean (storageKey: string, defaultValue = false): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [value, setValue] = useLocalStorage(storageKey, defaultValue ? 'true' : 'false')
  const parsed = value === 'true'
  return [parsed, newValue => setValue(typeof newValue === 'function' ? (newValue(parsed) ? 'true' : 'false') : (newValue ? 'true' : 'false'))]
}

export function useLocalStorageInteger (storageKey: string, defaultValue = 0): [number, Dispatch<SetStateAction<number>>] {
  const [value, setValue] = useLocalStorage(storageKey, String(defaultValue))
  const parsed = parseInt(value, 10)
  return [parsed, newValue => setValue(String(typeof newValue === 'function' ? newValue(parsed) : newValue))]
}
